import React, { useState, useEffect } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { getApiEndpoint } from "../../config/config";
import { useToast } from "../../context/ToastContext";
import PageContent from "../shared/PageContent/PageContent";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import {
  Card,
  Text,
  Group,
  Button,
  Stack,
  Box,
  Title,
  Divider,
  Badge,
  TextInput,
  Alert,
  Paper,
  Modal,
  Loader,
  Collapse,
  ActionIcon,
} from "@mantine/core";
import {
  IconUsers,
  IconUserShield,
  IconCreditCard,
  IconLock,
  IconCheck,
  IconAlertCircle,
  IconBrandStripe,
  IconCalendar,
  IconChevronDown,
} from "@tabler/icons-react";
import "./SubscriptionPage.css";
import { handleGenericError } from "../../utils/errorHandler";

// Initialize Stripe - replace with your actual publishable key
const stripePromise = loadStripe(
  process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || ""
);

// Pricing constants - will be replaced by API data
const DEFAULT_MANAGER_PRICE = 0;
const DEFAULT_REP_PRICE = 0;

// Stripe Card Element styling
const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      fontSize: "16px",
      color: "#424770",
      "::placeholder": {
        color: "#aab7c4",
      },
      fontFamily:
        '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    },
    invalid: {
      color: "#fa755a",
      iconColor: "#fa755a",
    },
  },
};

// Payment Method Form Component (uses Stripe Elements)
function PaymentMethodForm({ onSuccess, onRefresh }) {
  const stripe = useStripe();
  const elements = useElements();
  const { getVerifiedJWT } = useAuth();
  const { showSuccess, showError } = useToast();
  const [cardholderName, setCardholderName] = useState("");
  const [processing, setProcessing] = useState(false);
  const [cardComplete, setCardComplete] = useState(false);

  const handleSubmit = async e => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    if (!cardholderName.trim()) {
      showError("Please enter the cardholder name");
      return;
    }

    setProcessing(true);

    try {
      const jwt = await getVerifiedJWT();
      if (!jwt) {
        showError("Authentication failed");
        setProcessing(false);
        return;
      }

      // 1. Create SetupIntent on backend
      const setupIntentResponse = await fetch(
        getApiEndpoint("/v2/subscription/create-setup-intent"),
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${jwt}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!setupIntentResponse.ok) {
        throw new Error("Failed to create SetupIntent");
      }

      const setupIntentData = await setupIntentResponse.json();
      const client_secret = setupIntentData.data?.client_secret;

      if (!client_secret) {
        throw new Error("No client secret received from server");
      }

      // 2. Confirm the SetupIntent with Stripe
      const { error, setupIntent } = await stripe.confirmCardSetup(
        client_secret,
        {
          payment_method: {
            card: elements.getElement(CardElement),
            billing_details: {
              name: cardholderName,
            },
          },
        }
      );

      if (error) {
        handleGenericError(error, "Failed to add payment method");
        setProcessing(false);
        return;
      }

      // 3. Send payment method ID to backend to save
      const saveResponse = await fetch(
        getApiEndpoint("/v2/subscription/save-payment-method"),
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${jwt}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            payment_method_id: setupIntent.payment_method,
          }),
        }
      );

      if (!saveResponse.ok) {
        throw new Error("Failed to save payment method");
      }

      showSuccess("Payment method added successfully!");

      // Clear the form
      elements.getElement(CardElement).clear();
      setCardholderName("");

      // Trigger success callback
      if (onSuccess) {
        onSuccess();
      }

      // Refresh subscription data
      if (onRefresh) {
        onRefresh();
      }
    } catch (error) {
      handleGenericError(
        error,
        "Failed to add payment method. Please try again."
      );
    } finally {
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <Stack gap="md">
        <Alert
          icon={<IconAlertCircle size={14} />}
          color="yellow"
          variant="light"
        >
          <Text size="xs">
            No payment method on file. Add a card below to activate your
            subscription.
          </Text>
        </Alert>

        <TextInput
          label="Cardholder Name"
          placeholder="Jane Smith"
          value={cardholderName}
          onChange={e => setCardholderName(e.currentTarget.value)}
          leftSection={<IconCreditCard size={16} />}
          required
          disabled={processing}
        />

        <Box>
          <Text size="sm" fw={500} mb={5}>
            Card Details
          </Text>
          <Paper
            p="sm"
            radius="md"
            withBorder
            style={{
              borderColor: cardComplete ? "#228be6" : "#ced4da",
            }}
          >
            <CardElement
              options={CARD_ELEMENT_OPTIONS}
              onChange={e => setCardComplete(e.complete)}
            />
          </Paper>
        </Box>

        <Paper p="sm" radius="md" className="stripe-placeholder" withBorder>
          <Group gap="xs">
            <IconLock size={14} color="gray" />
            <Text size="xs" c="dimmed">
              Your payment info is encrypted and processed securely by Stripe.
              Reppi never stores your card details.
            </Text>
          </Group>
        </Paper>

        <Button
          type="submit"
          size="md"
          leftSection={
            processing ? (
              <Loader size={18} color="white" />
            ) : (
              <IconBrandStripe size={18} />
            )
          }
          className="stripe-pay-btn"
          fullWidth
          disabled={!stripe || processing || !cardComplete}
        >
          {processing ? "Processing..." : "Add Card"}
        </Button>

        <Group justify="center" gap="xs">
          <IconLock size={12} color="gray" />
          <Text size="xs" c="dimmed">
            256-bit SSL encryption · PCI DSS compliant
          </Text>
        </Group>
      </Stack>
    </form>
  );
}

// Update Payment Method Form Component (uses Stripe Elements)
function UpdatePaymentMethodForm({ onSuccess, onRefresh }) {
  const stripe = useStripe();
  const elements = useElements();
  const { getVerifiedJWT } = useAuth();
  const { showSuccess, showError } = useToast();
  const [cardholderName, setCardholderName] = useState("");
  const [processing, setProcessing] = useState(false);
  const [cardComplete, setCardComplete] = useState(false);

  const handleSubmit = async e => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    if (!cardholderName.trim()) {
      showError("Please enter the cardholder name");
      return;
    }

    setProcessing(true);

    try {
      const jwt = await getVerifiedJWT();
      if (!jwt) {
        showError("Authentication failed");
        setProcessing(false);
        return;
      }

      // 1. Create SetupIntent on backend
      const setupIntentResponse = await fetch(
        getApiEndpoint("/v2/subscription/create-setup-intent"),
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${jwt}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!setupIntentResponse.ok) {
        throw new Error("Failed to create SetupIntent");
      }

      const setupIntentData = await setupIntentResponse.json();
      const client_secret = setupIntentData.data?.client_secret;

      if (!client_secret) {
        throw new Error("No client secret received from server");
      }

      // 2. Confirm the SetupIntent with Stripe
      const { error, setupIntent } = await stripe.confirmCardSetup(
        client_secret,
        {
          payment_method: {
            card: elements.getElement(CardElement),
            billing_details: {
              name: cardholderName,
            },
          },
        }
      );

      if (error) {
        handleGenericError(error, "Failed to update payment method");
        setProcessing(false);
        return;
      }

      // 3. Send payment method ID to backend to save
      const saveResponse = await fetch(
        getApiEndpoint("/v2/subscription/save-payment-method"),
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${jwt}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            payment_method_id: setupIntent.payment_method,
          }),
        }
      );

      if (!saveResponse.ok) {
        throw new Error("Failed to save payment method");
      }

      showSuccess("Payment method updated successfully!");

      // Clear the form
      elements.getElement(CardElement).clear();
      setCardholderName("");

      // Trigger success callback
      if (onSuccess) {
        onSuccess();
      }

      // Refresh subscription data
      if (onRefresh) {
        onRefresh();
      }
    } catch (error) {
      handleGenericError(
        error,
        "Failed to update payment method. Please try again."
      );
    } finally {
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <Stack gap="md">
        <Alert
          icon={<IconAlertCircle size={14} />}
          color="blue"
          variant="light"
        >
          <Text size="xs">
            Enter your new card details below. Your existing payment method will
            be replaced.
          </Text>
        </Alert>

        <TextInput
          label="Cardholder Name"
          placeholder="Jane Smith"
          value={cardholderName}
          onChange={e => setCardholderName(e.currentTarget.value)}
          leftSection={<IconCreditCard size={16} />}
          required
          disabled={processing}
        />

        <Box>
          <Text size="sm" fw={500} mb={5}>
            Card Details
          </Text>
          <Paper
            p="sm"
            radius="md"
            withBorder
            style={{
              borderColor: cardComplete ? "#228be6" : "#ced4da",
            }}
          >
            <CardElement
              options={CARD_ELEMENT_OPTIONS}
              onChange={e => setCardComplete(e.complete)}
            />
          </Paper>
        </Box>

        <Paper p="sm" radius="md" className="stripe-placeholder" withBorder>
          <Group gap="xs">
            <IconLock size={14} color="gray" />
            <Text size="xs" c="dimmed">
              Your payment info is encrypted and processed securely by Stripe.
              Reppi never stores your card details.
            </Text>
          </Group>
        </Paper>

        <Button
          type="submit"
          size="md"
          leftSection={
            processing ? (
              <Loader size={18} color="white" />
            ) : (
              <IconBrandStripe size={18} />
            )
          }
          className="stripe-pay-btn"
          fullWidth
          disabled={!stripe || processing || !cardComplete}
        >
          {processing ? "Processing..." : "Update Card"}
        </Button>

        <Group justify="center" gap="xs">
          <IconLock size={12} color="gray" />
          <Text size="xs" c="dimmed">
            256-bit SSL encryption · PCI DSS compliant
          </Text>
        </Group>
      </Stack>
    </form>
  );
}

function SubscriptionPage() {
  const { user } = useAuth();

  // Only COMPANY_ADMIN can access this page
  if (!user || user.role !== "COMPANY_ADMIN") {
    return <Navigate to="/dashboard" replace />;
  }

  // Billing shows the same subscription management content for all company
  // admins, regardless of which modules are enabled.
  return <SubscriptionContent />;
}

function SubscriptionContent() {
  const { user, getVerifiedJWT } = useAuth();
  const { showSuccess } = useToast();
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [missingCriticalData, setMissingCriticalData] = useState(false);

  // Invoice history state
  const [invoiceHistory, setInvoiceHistory] = useState([]);
  const [invoiceHistoryLoading, setInvoiceHistoryLoading] = useState(true);
  const [expandedInvoiceIndex, setExpandedInvoiceIndex] = useState(null);

  // User list modal state
  const [userListModalOpen, setUserListModalOpen] = useState(false);
  const [userListType, setUserListType] = useState(null); // 'managers' or 'reps'

  // Update payment method modal state
  const [updatePaymentModalOpen, setUpdatePaymentModalOpen] = useState(false);

  // Payment Method section collapse state
  const [paymentMethodOpen, setPaymentMethodOpen] = useState(true);

  // Fetch subscription overview data
  useEffect(() => {
    const fetchSubscriptionOverview = async () => {
      try {
        setLoading(true);
        setError(null);

        const jwt = await getVerifiedJWT();
        if (!jwt) {
          setError("Authentication failed");
          return;
        }

        const response = await fetch(
          getApiEndpoint(`/v2/subscription/overview`),
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${jwt}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (!response.ok) {
          setLoading(false);
          throw new Error("Failed to fetch subscription data");
        }

        const result = await response.json();
        const data = result.data;

        // Validate critical billing fields are present
        const hasCriticalFields =
          data?.preferred_billing_date &&
          data?.next_billing_date &&
          data?.cycle_start_date &&
          data?.cycle_end_date;

        if (!hasCriticalFields) {
          setMissingCriticalData(true);
        } else {
          setMissingCriticalData(false);
        }

        setSubscriptionData(data);
      } catch (err) {
        handleGenericError(err, "Error fetching subscription overview.");
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (user?.company) {
      fetchSubscriptionOverview();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.company, getVerifiedJWT]);

  // Use fetched data or defaults
  const managerCount = subscriptionData?.active_manager_count ?? 0;
  const repCount = subscriptionData?.active_rep_count ?? 0;
  const managerRate = subscriptionData?.manager_rate ?? DEFAULT_MANAGER_PRICE;
  const repRate = subscriptionData?.rep_rate ?? DEFAULT_REP_PRICE;

  // Current cycle prorated rates
  const currentCycleProrated =
    subscriptionData?.current_cycle_prorated ?? false;
  const currentCycleProratedManagerRate =
    subscriptionData?.prorated_manager_rate ?? null;
  const currentCycleProratedRepRate =
    subscriptionData?.prorated_rep_rate ?? null;

  // Use current cycle amounts from API only (no fallback calculations)
  const managerTotal = subscriptionData?.current_cycle_manager_amount ?? 0;
  const repTotal = subscriptionData?.current_cycle_rep_amount ?? 0;
  const grandTotal = subscriptionData?.current_cycle_total_amount ?? 0;

  // Which billing sections are active for this company
  const subscriptionEnabled = subscriptionData?.subscription_enabled ?? false;
  const welcomeSurveysEnabled =
    subscriptionData?.welcome_surveys_enabled ?? false;

  // Welcome Surveys module billing — use API data
  const welcomeSurveyHostingFee =
    subscriptionData?.welcome_survey_hosting_fee ?? 0;
  const welcomeSurveyDiscount =
    subscriptionData?.welcome_survey_item_discount ?? 0;
  // One entry per rate tier the cycle's surveys fell into, each carrying the
  // surveys priced at that tier's rate.
  const welcomeSurveyItemTiers =
    subscriptionData?.welcome_survey_item_tiers ?? [];

  // Stripe connection status — use API data
  const stripeConnected = subscriptionData?.stripe_connected ?? false;

  // Payment card information
  const paymentCardBrand = subscriptionData?.payment_method?.brand ?? null;
  const paymentCardLastFour = subscriptionData?.payment_method?.last4 ?? null;
  const paymentWarning = subscriptionData?.payment_method?.warning ?? null;

  // Subscription active status
  const isActive = subscriptionData?.is_active ?? false;

  // Billing date info — use API data
  const billingDay = String(subscriptionData?.preferred_billing_date ?? "1");

  // Fetch invoice history
  useEffect(() => {
    const fetchInvoiceHistory = async () => {
      try {
        setInvoiceHistoryLoading(true);

        const jwt = await getVerifiedJWT();
        if (!jwt) {
          return;
        }

        const response = await fetch(
          getApiEndpoint("/v2/subscription/invoice-history"),
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${jwt}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (!response.ok) {
          throw new Error("Failed to fetch invoice history");
        }

        const result = await response.json();
        setInvoiceHistory(result.data || []);
      } catch (err) {
        handleGenericError(err, "Error fetching invoice history.");
        setInvoiceHistory([]);
      } finally {
        setInvoiceHistoryLoading(false);
      }
    };

    if (user?.company) {
      fetchInvoiceHistory();
    }
  }, [user?.company, getVerifiedJWT]);

  // Show managers or reps list
  const showUserList = type => {
    setUserListType(type);
    setUserListModalOpen(true);
  };

  // Get the user list based on type
  const getUserList = () => {
    if (userListType === "managers") {
      return subscriptionData?.active_managers || [];
    } else if (userListType === "reps") {
      return subscriptionData?.active_reps || [];
    }
    return [];
  };

  const formatLastLoggedIn = dateString => {
    if (!dateString) return "Never";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch (error) {
      return "Invalid date";
    }
  };

  const formatCurrency = amount =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
    }).format(amount);

  // Renders one line per welcome-survey rate tier: the surveys that fell
  // inside that tier and what they cost at its rate. Tier minimums are the
  // count already billed below them, so a tier starting at 0 covers the first
  // survey onward and the last tier is open-ended.
  const renderWelcomeSurveyTierLines = tiers =>
    tiers.map((tier, index) => {
      const tierCount = tier.survey_count ?? 0;
      const tierMin = tier.survey_amount_min ?? 0;
      const nextTierMin = tiers[index + 1]?.survey_amount_min ?? null;
      const tierRange =
        nextTierMin !== null
          ? `${(tierMin + 1).toLocaleString()}-${nextTierMin.toLocaleString()}`
          : `${(tierMin + 1).toLocaleString()}+`;

      return (
        <Group justify="space-between" key={`ws-tier-${tierMin}-${index}`}>
          <Group gap="xs">
            <IconCreditCard size={16} color="var(--mantine-color-gray-6)" />
            <Text size="sm">
              {tierCount} Survey{tierCount !== 1 ? "s" : ""} ×{" "}
              {formatCurrency(tier.per_survey_rate ?? 0)}
              {tiers.length > 1 ? ` (${tierRange})` : ""}
            </Text>
          </Group>
          <Text size="sm" fw={600}>
            {formatCurrency(tier.amount ?? 0)}
          </Text>
        </Group>
      );
    });

  // Renders the per-invoice bill breakdown shown when a history row is
  // expanded — mirrors the "Monthly Bill" line items using the invoice's data.
  const renderInvoiceBreakdown = invoice => {
    const invRepCount = invoice.active_rep_count ?? 0;
    const invManagerCount = invoice.active_manager_count ?? 0;
    const invRepRate = invoice.rep_rate ?? 0;
    const invManagerRate = invoice.manager_rate ?? 0;
    const invWsHostingFee = invoice.welcome_survey_hosting_fee_amount ?? 0;
    const invWsCount = invoice.welcome_survey_count ?? 0;
    const invWsItemRate = invoice.welcome_survey_per_item_rate ?? 0;
    const invWsItemTotal = invoice.welcome_survey_items_fee_total ?? 0;
    const invWsTiers = invoice.welcome_survey_item_tiers ?? [];
    const invWsDiscount = invoice.welcome_survey_discount_amount ?? 0;
    const invWsTotal = invoice.welcome_survey_total_amount ?? 0;
    const hasWelcomeSurveys =
      invWsTotal > 0 || invWsCount > 0 || invWsHostingFee > 0;

    return (
      <Stack gap="md">
        {/* Billing period */}
        {(invoice.subscription_start_date || invoice.subscription_end_date) && (
          <Text size="xs" c="dimmed">
            {invoice.subscription_start_date
              ? formatBillingDate(invoice.subscription_start_date)
              : "N/A"}{" "}
            -{" "}
            {invoice.subscription_end_date
              ? formatBillingDate(invoice.subscription_end_date)
              : "N/A"}
          </Text>
        )}

        <Divider />

        {/* Line items */}
        <Stack gap="md">
          {/* Subscription */}
          <Stack gap="xs">
            <Text size="xs" fw={700} tt="uppercase" c="dimmed">
              Subscription
            </Text>
            <Group justify="space-between">
              <Group gap="xs">
                <IconUserShield
                  size={16}
                  color="var(--mantine-color-violet-6)"
                />
                <Text size="sm">
                  {invManagerCount} Manager{invManagerCount !== 1 ? "s" : ""} ×{" "}
                  {formatCurrency(invManagerRate)}
                </Text>
              </Group>
              <Text size="sm" fw={600}>
                {formatCurrency(invManagerCount * invManagerRate)}
              </Text>
            </Group>

            <Group justify="space-between">
              <Group gap="xs">
                <IconUsers size={16} color="var(--mantine-color-blue-6)" />
                <Text size="sm">
                  {invRepCount} Rep{invRepCount !== 1 ? "s" : ""} ×{" "}
                  {formatCurrency(invRepRate)}
                </Text>
              </Group>
              <Text size="sm" fw={600}>
                {formatCurrency(invRepCount * invRepRate)}
              </Text>
            </Group>
          </Stack>

          {/* Welcome Surveys */}
          {hasWelcomeSurveys && (
            <Stack gap="xs">
              <Text size="xs" fw={700} tt="uppercase" c="dimmed">
                Welcome Surveys
              </Text>
              <Group justify="space-between">
                <Group gap="xs">
                  <IconCreditCard
                    size={16}
                    color="var(--mantine-color-gray-6)"
                  />
                  <Text size="sm">Hosting fee</Text>
                </Group>
                <Text size="sm" fw={600}>
                  {formatCurrency(invWsHostingFee)}
                </Text>
              </Group>

              {invWsTiers.length > 0 ? (
                renderWelcomeSurveyTierLines(invWsTiers)
              ) : (
                // Invoices billed before per-tier lines were recorded only have
                // the blended rate and total.
                <Group justify="space-between">
                  <Group gap="xs">
                    <IconCreditCard
                      size={16}
                      color="var(--mantine-color-gray-6)"
                    />
                    <Text size="sm">
                      {invWsCount} Survey{invWsCount !== 1 ? "s" : ""} ×{" "}
                      {formatCurrency(invWsItemRate)}
                    </Text>
                  </Group>
                  <Text size="sm" fw={600}>
                    {formatCurrency(invWsItemTotal)}
                  </Text>
                </Group>
              )}

              {invWsDiscount > 0 && (
                <Group justify="space-between">
                  <Group gap="xs">
                    <IconCreditCard
                      size={16}
                      color="var(--mantine-color-gray-6)"
                    />
                    <Text size="sm">Messages Discount</Text>
                  </Group>
                  <Text size="sm" fw={600}>
                    - {formatCurrency(invWsDiscount)}
                  </Text>
                </Group>
              )}
            </Stack>
          )}
        </Stack>

        <Divider />

        <Group justify="space-between" align="center">
          <Text fw={700} size="lg">
            Total
          </Text>
          <Text fw={800} size="xl" c="dark">
            {formatCurrency(invoice.total_amount ?? 0)}
          </Text>
        </Group>
      </Stack>
    );
  };

  // Refresh subscription data function
  const refreshSubscriptionData = async () => {
    try {
      const jwt = await getVerifiedJWT();
      if (!jwt) return;

      const response = await fetch(
        getApiEndpoint(`/v2/subscription/overview`),
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${jwt}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.ok) {
        const result = await response.json();
        setSubscriptionData(result.data);
      }
    } catch (error) {
      handleGenericError(error, "Error fetching subscription data.");
    }
  };

  // If there's an error or critical data is missing, show error message
  if (error || missingCriticalData) {
    return (
      <PageContent title="Subscription">
        <Alert
          icon={<IconAlertCircle size={20} />}
          title="Error with Your Subscription"
          color="red"
          variant="filled"
        >
          <Text size="sm" fw={500}>
            There is an error with your subscription. Please contact customer
            support for assistance.
          </Text>
        </Alert>
      </PageContent>
    );
  }

  // Show loading state
  if (loading) {
    return (
      <PageContent title="Subscription">
        <Group justify="center" py="xl">
          <Loader size="lg" />
        </Group>
      </PageContent>
    );
  }

  return (
    <PageContent title="Subscription">
      <Stack gap="xl">
        {/* Billing Not Set Up Warning Banner */}
        {!stripeConnected && (
          <Alert
            icon={<IconAlertCircle size={20} />}
            title="Billing Not Set Up"
            color="red"
            variant="filled"
          >
            <Text size="sm" fw={500}>
              Billing currently isn't set up. Add a card{" "}
              {subscriptionData?.cycle_end_date ? (
                <Text span size="sm" fw={500}>
                  before{" "}
                  <Text span fw={700} size="md">
                    {`${formatBillingDate(subscriptionData?.cycle_end_date)} `}
                  </Text>
                </Text>
              ) : (
                ""
              )}
              or your Reppi account will be deactivated.
            </Text>
          </Alert>
        )}

        {/* ── Payment Method ── */}
        <Card padding="lg" radius="md" withBorder>
          <Stack gap="md">
            <Group
              justify="space-between"
              align="center"
              style={{ cursor: "pointer" }}
              onClick={() => setPaymentMethodOpen(o => !o)}
            >
              <Group gap="xs">
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  aria-label={
                    paymentMethodOpen
                      ? "Collapse payment method"
                      : "Expand payment method"
                  }
                  onClick={e => {
                    e.stopPropagation();
                    setPaymentMethodOpen(o => !o);
                  }}
                >
                  <IconChevronDown
                    size={18}
                    style={{
                      transition: "transform 200ms ease",
                      transform: paymentMethodOpen
                        ? "rotate(180deg)"
                        : "rotate(0deg)",
                    }}
                  />
                </ActionIcon>
                <Title order={4}>Payment Method</Title>
              </Group>
              <Group gap="xs">
                <IconBrandStripe size={20} color="#635BFF" />
                <Text size="sm" c="dimmed">
                  Powered by Stripe
                </Text>
              </Group>
            </Group>

            <Collapse in={paymentMethodOpen}>
              <Stack gap="md">
                <Divider />

                {stripeConnected ? (
                  /* Connected state */
                  <Stack gap="sm">
                    <Alert
                      icon={<IconCheck size={16} />}
                      color="green"
                      variant="light"
                    >
                      Your payment method is connected and up to date.
                    </Alert>
                    <Group gap="sm">
                      <IconCreditCard size={20} />
                      <Text size="sm">
                        {paymentCardBrand
                          ? `${paymentCardBrand.charAt(0).toUpperCase()}${paymentCardBrand.slice(1)}`
                          : "Card"}{" "}
                        ending in •••• {paymentCardLastFour || "••••"}
                      </Text>
                      <Badge color="green" variant="dot" size="sm">
                        Active
                      </Badge>
                    </Group>

                    {paymentWarning && (
                      <Alert
                        icon={<IconAlertCircle size={14} />}
                        color="yellow"
                        variant="light"
                        p="xs"
                      >
                        <Text size="xs">{paymentWarning}</Text>
                      </Alert>
                    )}

                    <Button
                      variant="outline"
                      color="gray"
                      leftSection={<IconCreditCard size={16} />}
                      size="sm"
                      onClick={() => setUpdatePaymentModalOpen(true)}
                    >
                      Update Payment Method
                    </Button>
                  </Stack>
                ) : (
                  /* Not connected — show Stripe Elements form */
                  <Elements stripe={stripePromise}>
                    <PaymentMethodForm
                      onSuccess={() => {
                        showSuccess("Payment method added successfully");
                      }}
                      onRefresh={refreshSubscriptionData}
                    />
                  </Elements>
                )}
              </Stack>
            </Collapse>
          </Stack>
        </Card>

        {/* ── Monthly Bill ── */}
        <Card padding="lg" radius="md" withBorder>
          <Stack gap="md">
            <Group justify="space-between" align="center">
              <Title order={4}>Monthly Bill</Title>
              <Badge
                color={isActive ? "green" : "red"}
                variant="light"
                size="lg"
              >
                {isActive ? "Active" : "Inactive"}
              </Badge>
            </Group>

            {/* Current billing period */}
            {subscriptionData?.cycle_start_date &&
              subscriptionData?.cycle_end_date && (
                <Text size="xs" c="dimmed">
                  {formatBillingDate(subscriptionData.cycle_start_date)} -{" "}
                  {formatBillingDate(subscriptionData.cycle_end_date)}
                </Text>
              )}

            <Divider />

            {/* Line items */}
            <Stack gap="md">
              {/* Subscription */}
              {subscriptionEnabled && (
                <Stack gap="xs">
                  <Text size="xs" fw={700} tt="uppercase" c="dimmed">
                    Subscription
                  </Text>
                  <Group
                    justify="space-between"
                    style={{ cursor: "pointer" }}
                    onClick={() => showUserList("managers")}
                  >
                    <Group gap="xs">
                      <IconUserShield
                        size={16}
                        color="var(--mantine-color-violet-6)"
                      />
                      <Text size="sm">
                        {managerCount} Manager{managerCount !== 1 ? "s" : ""} ×{" "}
                        {formatCurrency(
                          currentCycleProrated &&
                            currentCycleProratedManagerRate !== null
                            ? currentCycleProratedManagerRate
                            : managerRate
                        )}
                      </Text>
                    </Group>
                    <Text size="sm" fw={600}>
                      {formatCurrency(managerTotal)}
                    </Text>
                  </Group>

                  <Group
                    justify="space-between"
                    style={{ cursor: "pointer" }}
                    onClick={() => showUserList("reps")}
                  >
                    <Group gap="xs">
                      <IconUsers
                        size={16}
                        color="var(--mantine-color-blue-6)"
                      />
                      <Text size="sm">
                        {repCount} Rep{repCount !== 1 ? "s" : ""} ×{" "}
                        {formatCurrency(
                          currentCycleProrated &&
                            currentCycleProratedRepRate !== null
                            ? currentCycleProratedRepRate
                            : repRate
                        )}
                      </Text>
                    </Group>
                    <Text size="sm" fw={600}>
                      {formatCurrency(repTotal)}
                    </Text>
                  </Group>
                </Stack>
              )}

              {/* Welcome Surveys */}
              {welcomeSurveysEnabled && (
                <Stack gap="xs">
                  <Text size="xs" fw={700} tt="uppercase" c="dimmed">
                    Welcome Surveys
                  </Text>
                  <Group justify="space-between">
                    <Group gap="xs">
                      <IconCreditCard
                        size={16}
                        color="var(--mantine-color-gray-6)"
                      />
                      <Text size="sm">Hosting fee</Text>
                    </Group>
                    <Text size="sm" fw={600}>
                      {formatCurrency(welcomeSurveyHostingFee)}
                    </Text>
                  </Group>

                  {renderWelcomeSurveyTierLines(welcomeSurveyItemTiers)}
                  {welcomeSurveyDiscount > 0 && (
                    <Group justify="space-between">
                      <Group gap="xs">
                        <IconCreditCard
                          size={16}
                          color="var(--mantine-color-gray-6)"
                        />
                        <Text size="sm">Messages Discount</Text>
                      </Group>
                      <Text size="sm" fw={600}>
                        - {formatCurrency(welcomeSurveyDiscount)}
                      </Text>
                    </Group>
                  )}
                </Stack>
              )}
            </Stack>

            <Divider />

            <Group justify="space-between" align="center">
              <Text fw={700} size="lg">
                Total Due
              </Text>
              <Text fw={800} size="xl" c="dark">
                {formatCurrency(grandTotal)}
                <Text span size="sm" c="dimmed" fw={400}></Text>
              </Text>
            </Group>

            {/* Current due date or disabled message */}
            {isActive ? (
              subscriptionData?.cycle_end_date && (
                <Group justify="space-between" align="center">
                  <Group gap="xs">
                    <IconCalendar size={16} color="gray" />
                    <Text size="sm" c="dimmed">
                      Due Date
                    </Text>
                  </Group>
                  <Text size="sm" fw={600} c="dark">
                    {formatBillingDate(subscriptionData.cycle_end_date)}
                  </Text>
                </Group>
              )
            ) : (
              <Alert
                icon={<IconAlertCircle size={14} />}
                color="gray"
                variant="light"
                p="xs"
              >
                <Text size="xs">
                  Billing is currently disabled, your card will not be charged.
                </Text>
              </Alert>
            )}

            {/* Billing cycle info */}
            <Paper p="sm" radius="md" bg="gray.0" withBorder={false}>
              <Group gap="xs">
                <IconCalendar size={14} color="gray" />
                <Text size="xs" c="dimmed">
                  Billed on the{" "}
                  <Text span fw={600} c="dark" size="xs">
                    {ordinal(parseInt(billingDay, 10))} of each month
                  </Text>
                </Text>
              </Group>
            </Paper>
          </Stack>
        </Card>

        {/* ── Invoice History ── */}
        <Card padding="lg" radius="md" withBorder>
          <Stack gap="md">
            <Group justify="space-between" align="center">
              <Title order={4}>Invoice History</Title>
              {/* <Button
                variant="subtle"
                size="xs"
                leftSection={<IconReceipt size={14} />}
                disabled
              >
                Download All
              </Button> */}
            </Group>
            <Divider />

            {invoiceHistoryLoading ? (
              <Group justify="center" py="xl">
                <Loader size="md" />
              </Group>
            ) : invoiceHistory.length === 0 ? (
              <Text size="sm" c="dimmed" ta="center" py="md">
                No invoice history available.
              </Text>
            ) : (
              <Box style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ borderBottom: "1px solid #dee2e6" }}>
                      <th
                        style={{
                          padding: "12px 8px",
                          textAlign: "left",
                          fontSize: "12px",
                          fontWeight: 600,
                          color: "#868e96",
                        }}
                      >
                        Payment Date
                      </th>
                      <th
                        style={{
                          padding: "12px 8px",
                          textAlign: "left",
                          fontSize: "12px",
                          fontWeight: 600,
                          color: "#868e96",
                        }}
                      >
                        Period
                      </th>
                      <th
                        style={{
                          padding: "12px 8px",
                          textAlign: "left",
                          fontSize: "12px",
                          fontWeight: 600,
                          color: "#868e96",
                        }}
                      >
                        Reps
                      </th>
                      <th
                        style={{
                          padding: "12px 8px",
                          textAlign: "left",
                          fontSize: "12px",
                          fontWeight: 600,
                          color: "#868e96",
                        }}
                      >
                        Managers
                      </th>
                      <th
                        style={{
                          padding: "12px 8px",
                          textAlign: "left",
                          fontSize: "12px",
                          fontWeight: 600,
                          color: "#868e96",
                        }}
                      >
                        Welcome Surveys
                      </th>
                      <th
                        style={{
                          padding: "12px 8px",
                          textAlign: "right",
                          fontSize: "12px",
                          fontWeight: 600,
                          color: "#868e96",
                        }}
                      >
                        Total
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoiceHistory.map((invoice, index) => {
                      const isExpanded = expandedInvoiceIndex === index;
                      return (
                        <React.Fragment key={index}>
                          <tr
                            style={{
                              borderBottom: "1px solid #f1f3f5",
                              cursor: "pointer",
                            }}
                            onClick={() =>
                              setExpandedInvoiceIndex(isExpanded ? null : index)
                            }
                          >
                            <td style={{ padding: "12px 8px" }}>
                              <Group gap="xs" wrap="nowrap">
                                <IconChevronDown
                                  size={14}
                                  color="gray"
                                  style={{
                                    transition: "transform 150ms ease",
                                    transform: isExpanded
                                      ? "rotate(180deg)"
                                      : "none",
                                  }}
                                />
                                <Text size="sm" fw={600}>
                                  {invoice.payment_date
                                    ? formatBillingDate(invoice.payment_date)
                                    : "N/A"}
                                </Text>
                              </Group>
                            </td>
                            <td style={{ padding: "12px 8px" }}>
                              <Text size="sm" c="dimmed">
                                {invoice.subscription_start_date
                                  ? formatBillingDate(
                                      invoice.subscription_start_date
                                    )
                                  : "N/A"}
                                {" – "}
                                {invoice.subscription_end_date
                                  ? formatBillingDate(
                                      invoice.subscription_end_date
                                    )
                                  : "N/A"}
                              </Text>
                            </td>
                            <td style={{ padding: "12px 8px" }}>
                              <Text size="sm">
                                {invoice.active_rep_count ?? 0}
                              </Text>
                              <Text size="sm" c="dimmed">
                                {formatCurrency(invoice.rep_rate ?? 0)}
                              </Text>
                            </td>
                            <td style={{ padding: "12px 8px" }}>
                              <Text size="sm">
                                {invoice.active_manager_count ?? 0}
                              </Text>
                              <Text size="sm" c="dimmed">
                                {formatCurrency(invoice.manager_rate ?? 0)}
                              </Text>
                            </td>
                            <td style={{ padding: "12px 8px" }}>
                              <Text size="sm" fw={600}>
                                {formatCurrency(
                                  invoice.welcome_survey_total_amount ?? 0
                                )}
                              </Text>
                            </td>
                            <td
                              style={{
                                padding: "12px 8px",
                                textAlign: "right",
                              }}
                            >
                              <Text size="sm" fw={600}>
                                {formatCurrency(invoice.total_amount ?? 0)}
                              </Text>
                            </td>
                          </tr>
                          {isExpanded && (
                            <tr style={{ borderBottom: "1px solid #f1f3f5" }}>
                              <td colSpan={6} style={{ padding: 0 }}>
                                <Box p="lg" bg="gray.0">
                                  {renderInvoiceBreakdown(invoice)}
                                </Box>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </Box>
            )}
          </Stack>
        </Card>

        {/* User List Modal */}
        <Modal
          opened={userListModalOpen}
          onClose={() => setUserListModalOpen(false)}
          title={userListType === "managers" ? "Managers" : "Reps"}
          size="lg"
          centered
        >
          <Stack gap="md">
            {getUserList().length === 0 ? (
              <Text size="sm" c="dimmed" ta="center" py="md">
                No {userListType} found.
              </Text>
            ) : (
              <Stack gap="xs">
                {getUserList().map((user, index) => (
                  <Paper key={index} p="md" withBorder>
                    <Group justify="space-between" align="center">
                      <Box>
                        <Text size="sm" fw={600}>
                          {user.name || "Unknown User"}
                        </Text>
                        <Text size="xs" c="dimmed">
                          Last logged in:{" "}
                          {formatLastLoggedIn(user.last_logged_in)}
                        </Text>
                      </Box>
                    </Group>
                  </Paper>
                ))}
              </Stack>
            )}
          </Stack>
        </Modal>

        {/* Update Payment Method Modal */}
        <Modal
          opened={updatePaymentModalOpen}
          onClose={() => setUpdatePaymentModalOpen(false)}
          title="Update Payment Method"
          size="md"
          centered
        >
          <Elements stripe={stripePromise}>
            <UpdatePaymentMethodForm
              onSuccess={() => {
                setUpdatePaymentModalOpen(false);
              }}
              onRefresh={refreshSubscriptionData}
            />
          </Elements>
        </Modal>
      </Stack>
    </PageContent>
  );
}

/**
 * Formats a date string from the API into a readable format.
 * Expected input format: ISO date string (e.g., "2024-03-15T00:00:00.000Z")
 */
function formatBillingDate(dateString) {
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
      timeZone: "UTC",
    });
  } catch (error) {
    return dateString;
  }
}

/** Returns an ordinal string for a number, e.g. 1 → "1st", 2 → "2nd" */
function ordinal(n) {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

export default SubscriptionPage;
